import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion, type Variants } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { NAVIGATION_ITEMS } from '../../utils/constants';
import { companyInfo } from '../../data/companyInfo';
import Button from '../ui/Button';
import { LogoLockup } from '../ui/Logo';
import ThemeToggle from '../ui/ThemeToggle';

interface NavigationProps {
  isScrolled: boolean;
}

const overlayLinks: Variants = {
  hidden: { opacity: 0, x: -16 },
  visible: { opacity: 1, x: 0 },
};

const Navigation = ({ isScrolled }: NavigationProps) => {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const desktopLinkClass = (isActive: boolean) =>
    `rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${
      isActive
        ? isScrolled
          ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
          : 'bg-white/15 text-white'
        : isScrolled
          ? 'text-content-muted hover:bg-surface-alt hover:text-brand-700 dark:hover:text-brand-300'
          : 'text-slate-200 hover:bg-white/10 hover:text-white'
    }`;

  // Icon-button colors that adapt to the header background state.
  const iconBtnClass = isScrolled
    ? 'text-content-muted hover:bg-surface-alt'
    : 'text-white/90 hover:bg-white/10 hover:text-white';

  return (
    <nav className="container-tight" aria-label="Primary">
      <div className="flex h-20 items-center justify-between">
        <Link to="/" aria-label={`${companyInfo.name} — home`}>
          <LogoLockup tone={isScrolled ? 'dark' : 'light'} />
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-1 md:flex">
          {NAVIGATION_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => desktopLinkClass(isActive)}
            >
              {item.label}
            </NavLink>
          ))}
          <ThemeToggle className={`ml-1 ${iconBtnClass}`} />
          <Button
            size="sm"
            variant={isScrolled ? 'primary' : 'white'}
            className="ml-2"
            onClick={() => navigate('/contact')}
          >
            Get a Quote
          </Button>
        </div>

        {/* Mobile controls */}
        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle className={iconBtnClass} />
          <button
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            className={`rounded-lg p-2 transition-colors ${iconBtnClass}`}
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile full-screen overlay (always dark) */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col bg-slate-950/98 backdrop-blur-xl md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="container-tight flex h-20 items-center justify-between">
              <Link to="/" onClick={() => setOpen(false)} aria-label={`${companyInfo.name} — home`}>
                <LogoLockup tone="light" />
              </Link>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="rounded-lg p-2 text-white hover:bg-white/10"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <motion.div
              className="container-tight flex flex-1 flex-col justify-center gap-1 pb-24"
              initial={reduce ? false : 'hidden'}
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } } }}
            >
              {NAVIGATION_ITEMS.map((item) => (
                <motion.div key={item.to} variants={overlayLinks}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-3 text-3xl font-semibold transition-colors ${
                        isActive ? 'text-accent-400' : 'text-white hover:text-accent-400'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div variants={overlayLinks}>
                <Button
                  size="lg"
                  variant="primary"
                  className="mt-8 w-full"
                  onClick={() => {
                    setOpen(false);
                    navigate('/contact');
                  }}
                >
                  Get in Touch
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
